import { useState } from "react"
import { useLocation } from "wouter"
import { postFeedbackInvitation } from "../api"
import {
  ApiResponseError,
  isExpectedApiError,
  isUnauthorized,
} from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import { paths } from "../../../paths"

/**
 * Mints the feedback invitation a client is about to fill in. Both kiosk entry
 * points need it — tapping a single-participant session, and picking yourself out
 * of a group session's participants — with the same failure handling, which is
 * why it lives here rather than being duplicated in each page.
 *
 * `onCreated` is where the two callers differ: one hands the id up to the app
 * shell, the other navigates straight to the form.
 */
export function useCreateInvitation(onCreated: (invitationId: string) => void) {
  const [error, setError] = useState<string | null>(null)
  const [, navigate] = useLocation()

  async function create(sessionId: string, contactId: string): Promise<void> {
    try {
      onCreated(await postFeedbackInvitation(sessionId, contactId))
    } catch (err) {
      // Same policy as useKioskQuery, deliberately restated rather than shared:
      // two occurrences isn't enough to know the right abstraction. A third
      // device-authenticated call is the cue to extract it.
      if (isUnauthorized(err)) {
        navigate(paths.kioskLogin)
        return
      }
      if (!isExpectedApiError(err))
        trackError(err, { source: "postFeedbackInvitation" })
      setError(
        err instanceof ApiResponseError ? err.message : "Something went wrong.",
      )
    }
  }

  return { create, error }
}
